import React from 'react'
import { ChevronRight, RefreshCw, Loader2, PlayCircle, Square, Edit } from 'lucide-react'
import { Badge } from '../../../../../../components/ui/Badge'
import { getOrderDisplayStatus } from '../../utils/orderStatus'

/**
 * Compact header for the order detail view.
 * Combines: order info, back button, refresh button, and OF Start/End buttons.
 */
function OrderDetailHeader({
  selectedOrder,
  selectedLine,
  ofStartDateTime,
  ofEndDateTime,
  formatDateTime,
  loadingConsumptions,
  loadingRecipe,
  onBack,
  onRefresh,
  onOpenStartModal,
  onOpenEndModal,
  readOnly = false,
}) {
  const { label: statusLabel, badgeClass: statusBadgeClass } = getOrderDisplayStatus(selectedOrder)

  return (
    <div className="mb-6 bg-gray-800 rounded-lg border border-gray-700">
      <div className="p-4">
        {/* Top row: Back button + Order info + Refresh */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-3">
            <button
              onClick={onBack}
              className="p-2 hover:bg-gray-700 rounded-lg transition-colors"
            >
              <ChevronRight className="w-5 h-5 text-gray-400 rotate-180" />
            </button>
            <div>
              <div className="flex items-center gap-2">
                <h2 className="text-xl font-bold text-white font-mono">{selectedOrder.OrderNumber}</h2>
                <Badge className={statusBadgeClass}>
                  {statusLabel}
                </Badge>
                {selectedOrder.MeasurementUnit && (
                  <Badge variant="info">{selectedOrder.MeasurementUnit}</Badge>
                )}
              </div>
              <p className="text-sm text-gray-400">
                {selectedOrder.ProductDescription || selectedOrder.ProductName} | {selectedLine.line} - {selectedLine.factory}
              </p>
            </div>
          </div>
          <button
            onClick={onRefresh}
            disabled={loadingConsumptions || loadingRecipe}
            className="flex items-center gap-2 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-700 disabled:cursor-not-allowed rounded-lg transition-colors text-white text-sm"
          >
            {(loadingConsumptions || loadingRecipe) ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <RefreshCw className="w-4 h-4" />
            )}
            <span>Actualizar</span>
          </button>
        </div>

        {/* Bottom row: OF Start / End */}
        <div className="flex items-center gap-3 pt-3 border-t border-gray-700">
          {readOnly ? (
            <>
              <div className="flex-1 flex items-center gap-2 px-3 py-2 bg-gray-700/50 rounded-lg text-sm">
                <PlayCircle className="w-4 h-4 text-green-400" />
                <div>
                  <div className="font-medium text-sm text-gray-300">Inicio OF</div>
                  <div className="text-xs text-gray-400">{formatDateTime(ofStartDateTime)}</div>
                </div>
              </div>
              <div className="flex-1 flex items-center gap-2 px-3 py-2 bg-gray-700/50 rounded-lg text-sm">
                <Square className="w-4 h-4 text-red-400" />
                <div>
                  <div className="font-medium text-sm text-gray-300">Fin OF</div>
                  <div className="text-xs text-gray-400">{formatDateTime(ofEndDateTime)}</div>
                </div>
              </div>
            </>
          ) : (
            <>
              <button
                onClick={onOpenStartModal}
                className="flex-1 flex items-center justify-between gap-2 px-3 py-2 bg-green-600/80 hover:bg-green-600 rounded-lg transition-colors text-white text-sm"
              >
                <div className="flex items-center gap-2">
                  <PlayCircle className="w-4 h-4" />
                  <div className="text-left">
                    <div className="font-medium text-sm">Inicio OF</div>
                    <div className="text-xs text-green-100">{formatDateTime(ofStartDateTime)}</div>
                  </div>
                </div>
                <Edit className="w-3.5 h-3.5 opacity-60" />
              </button>

              <button
                onClick={onOpenEndModal}
                className="flex-1 flex items-center justify-between gap-2 px-3 py-2 bg-red-600/80 hover:bg-red-600 rounded-lg transition-colors text-white text-sm"
              >
                <div className="flex items-center gap-2">
                  <Square className="w-4 h-4" />
                  <div className="text-left">
                    <div className="font-medium text-sm">Fin OF</div>
                    <div className="text-xs text-red-100">{formatDateTime(ofEndDateTime)}</div>
                  </div>
                </div>
                <Edit className="w-3.5 h-3.5 opacity-60" />
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

export default React.memo(OrderDetailHeader)
